import { useState, useEffect } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { logger } from '../utils/logger';

interface UserRole {
  user: FirebaseUser | null;
  role: 'admin' | 'customer' | null;
  loading: boolean;
}

export const useUserRole = (): UserRole => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [role, setRole] = useState<'admin' | 'customer' | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      logger.log('🔐 Auth state changed:', firebaseUser ? firebaseUser.email : 'No user');
      setUser(firebaseUser);
      
      if (firebaseUser) {
        try {
          logger.log('📄 Fetching user document for:', firebaseUser.uid);
          // Get user role from Firestore
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          logger.log('📄 User document exists:', userDoc.exists());
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            logger.log('📄 User data:', userData);
            const userRole = userData.userType || userData.role || 'customer';
            logger.log('🎭 Detected role:', userRole);
            setRole(userRole === 'admin' ? 'admin' : 'customer');
          } else {
            logger.log('⚠️ No user document found, defaulting to customer');
            // If no document exists, default to customer
            setRole('customer');
          }
        } catch (error) {
          logger.error('❌ Error fetching user role:', error);
          setRole('customer');
        }
      } else {
        logger.log('👤 No user logged in');
        setRole(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { user, role, loading };
};
